import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import MobileTabBar from "./MobileTabBar";
import MobileAccountMenu from "./MobileAccountMenu";
import PageTransition from "./PageTransition";
import { PullFrame } from "./pullToRefresh";
import { useIsMobile } from "../ui/useIsMobile";
import { TabBarVisibilityContext } from "./tabBarVisibility";
import { isSectionRoot } from "./activePath";
import { UnsavedChangesProvider } from "./UnsavedChanges";
import { AccountMenuContext } from "./accountMenu";
import { useWayInShell } from "./wayInShell";
import useCustomScrollbar from "../../styles/useCustomScrollbar";
import "../../styles/customScrollbar.css";

/**
 * Shell for every signed-in screen. From `md` up the sidebar is a static rail
 * and there is nothing else around the page.
 *
 * Below it the screen is laid out the way a phone is: the page from the top of
 * the window down, and the four commonest sections across the bottom — three in
 * a floating pill and My task in a circle of its own — on those four sections
 * and on nothing opened from one. See `barShown`. The rail is not there at
 * all, and neither is a bar of the shell's own: the three-line button that
 * opens `MobileAccountMenu` — which carries the account itself and the five
 * destinations the tab bar has no room for — is drawn by whatever row heads the
 * screen, so the greeting or the title and the way into the menu are one line
 * rather than two. See `accountMenu`.
 */
/**
 * The mark on a history entry that has the account menu open over it. The
 * menu is a step in the history rather than a flag in state — see
 * `AppLayout` — and this is how the entry says so. Not a route: the URL is
 * the page underneath, which is what a reload should show.
 */
const ACCOUNT_MENU_STATE = "accountMenu";

const menuOpenIn = (state: unknown): boolean =>
  (state as { [ACCOUNT_MENU_STATE]?: unknown } | null)?.[ACCOUNT_MENU_STATE] ===
  true;

const AppLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // The account menu is a screen of its own on a phone — it slides in over
  // the app and the screens it opens slide in over it — so it is a step in
  // the history like one, not a flag held here. Opening it pushes an entry
  // for the same URL with the mark on it; a row pressed on it pushes the
  // destination on top of that; and Back walks the same way down: to the
  // menu, then to the page it was opened over. Held as a flag instead, Back
  // from Settings skipped the menu and landed on whatever tab bar screen came
  // before it — two steps for one press. Whether the menu is open is read
  // straight off the entry, so navigating anywhere from it closes it, the
  // `?tab=`-only rows included.
  const menuOpen = menuOpenIn(location.state);
  const openMenu = () =>
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        // The page's own state comes along, since the page is still what is
        // drawn under the menu and may be reading it — a plan's origin, say.
        state: {
          ...((location.state as object | null) ?? {}),
          [ACCOUNT_MENU_STATE]: true,
        },
      },
    );
  const closeMenu = () => {
    // Back to the entry underneath, which is the page without the mark. The
    // router numbers its entries; an entry with no number under it — a
    // memory router, or a history this tab did not write — is unmarked in
    // place instead, so the menu can always be closed.
    const index = (window.history.state as { idx?: unknown } | null)?.idx;
    if (typeof index === "number" && index > 0) {
      navigate(-1);
      return;
    }
    const { [ACCOUNT_MENU_STATE]: _open, ...rest } =
      (location.state as { [ACCOUNT_MENU_STATE]?: unknown } | null) ?? {};
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: rest },
    );
  };

  // The page's scrolling panel, for the pull-to-refresh that moves the whole
  // screen above the tab bar — see `PullFrame`. Phones only.
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const isMobile = useIsMobile();

  /**
   * Whether this is the paid end of a new practice's way in rather than a
   * screen of the app: the checkout a term is bought on straight off the
   * offer, and the confirmation after it.
   *
   * It turns the whole of the app off — the rail, the floating bar, the
   * account menu and the off-white fill behind the page — and leaves the
   * shell as what the trial's own screens are: a white page, and the step on
   * it. A buyer who has never been inside the app is not shown the app in the
   * middle of buying it, and the three screens of the flow read as one thing
   * because the furniture around them never changes. The rail arrives once,
   * on the dashboard both confirmations lead to. See `wayInShell`.
   */
  const wayIn = useWayInShell(location.pathname, location.state);

  // Attaches the hover/scroll reveal behaviour to every
  // `.custom-scrollbar-overlay` in the tree, including ones mounted later.
  useCustomScrollbar();

  // How many screens below are holding the foot of the phone — see
  // `tabBarVisibility`. Counted rather than flagged, so one form replacing
  // another cannot leave the bar hidden behind it.
  const [claims, setClaims] = useState(0);
  const hide = useCallback(() => {
    setClaims((held) => held + 1);
    // Released once, however many times React hands the cleanup back.
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setClaims((held) => held - 1);
    };
  }, []);
  const visibility = useMemo(() => ({ hide }), [hide]);

  /**
   * Whether the bar is drawn at all on this screen.
   *
   * It is the phone's way *between* sections, so it belongs on the sections
   * and nowhere under them. A client, an enquiry, a plan, the day's
   * appointment, Settings and its Billings tab, Revenue, My brand — each of
   * those was opened from somewhere and is somewhere to come back out of,
   * which is what the back arrow in its title row says. A bar offering three
   * other sections across the foot of such a screen offers a sideways step
   * out of a place the reader is still standing in, and it takes 68px of a
   * phone's height to do it on exactly the screens that have the most to
   * show: a record with its tabs, or a form.
   *
   * `SECTION_ROOTS` is the list, and it is the same one `PageHeader` draws
   * the arrow from — a screen either heads itself with a way back or stands
   * on the bar, never both and never neither. Read off `location.pathname`
   * rather than `useActivePath`, because what matters here is the screen the
   * reader is on and not which section lights for it: a plan opened from a
   * client is still a plan, however the rail marks it.
   *
   * A query string is not part of a pathname, so a tab of a section root
   * keeps the bar and `/settings?tab=billings` does not have one to lose.
   */
  const atSectionRoot = isSectionRoot(location.pathname);

  // Drawn unless a screen below is holding the foot of the phone for its own
  // Save row, the way in has the shell to itself, or this is not one of the
  // screens the bar belongs on.
  const barShown = claims === 0 && !wayIn && atSectionRoot;

  // The shell is pinned to the viewport, so the window itself must never
  // scroll — otherwise a page-level scrollbar appears alongside the shell's
  // own and drags the layout off the top. Auth screens are outside this
  // layout and keep normal document scrolling.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return (
    // Around both the rail and the routed page, which is the whole point: the
    // form below registers what it is holding, and the rail above asks about it
    // before it navigates. See `UnsavedChanges`.
    <UnsavedChangesProvider>
      {/* `fixed inset-0` rather than `h-screen`: out of flow, the shell can
          never add height to the document, so no descendant can push a window
          scrollbar or leave empty page below it. */}
      <div className="fixed inset-0 flex overflow-hidden bg-white">
        {!wayIn && <Sidebar />}

        {/* min-w-0 lets wide children (the clients table) scroll inside
            themselves instead of stretching this column past the viewport.
            The off-white fill is what separates the page from the white cards
            sitting on it — and is the one thing a step of the way in does not
            want, because the two screens on either side of it are white: the
            offer before it and the confirmation after. The fill is handed to
            the panels below as `--page-fill` rather than set here, since it is
            the scrolling panel that is actually painted. */}
        <div
          className="relative flex-1 min-w-0 flex flex-col overflow-hidden bg-[color:var(--page-fill)]"
          // How much room the page keeps at its foot for the tab bar floating
          // over it. The bar is out of this column's flow — it has to be, or
          // the page would stop where it begins and there would be nothing
          // under the glass to frost — so the space it occupies is given back
          // to the page here and spent by `.page-panel` in `index.css`.
          //
          // Zero wherever no bar is drawn — a screen holding the bottom edge
          // for its own Save and Cancel row, or any screen under a section
          // rather than one of the sections themselves. Room kept for a bar
          // that is not there is a band of nothing at the end of the page,
          // and on a detail screen it is 68px of the most crowded screens in
          // the app. Set on the column rather than on the panel because the
          // panel is remade for every screen; it does change with the screen
          // now, which the column re-renders for either way.
          style={
            {
              "--tab-bar-room": barShown ? "var(--tab-bar-height)" : "0px",
              // The fill the page is painted on, read by this column and by
              // every panel inside it — see `PageTransition`. One declaration
              // for both so a screen sliding in can never be a different
              // colour from the one it is sliding over.
              "--page-fill": wayIn ? "#FFFFFF" : "#FCFCFD",
            } as React.CSSProperties
          }
        >
          {/* The page moves under a pull-to-refresh the way a phone app's
              screen does; the tab bar below stays where it is. */}
          <PullFrame panel={panel} enabled={isMobile}>
            {/* The notch and the camera, and nothing else. The shell drew a
                bar of its own here until the screens took the button off it —
                what is left to keep is the room a phone needs above the first
                line of the page, which `env(...)` reports as 0px on a phone
                with nothing up there. It needs `viewport-fit=cover` in
                `index.html` to report anything at all; the tab bar at the foot
                of this column takes the matching inset at the other end.

                A sibling above the scrolling panel rather than padding inside
                it, so the inset is held however far the page is scrolled and
                nothing can be read through the status bar. */}
            <div className="md:hidden h-[env(safe-area-inset-top)] flex-shrink-0" />

            {/* The page, running the full height of the window with the tab
                bar floating over its foot. The room it keeps for that bar is
                `--tab-bar-room` above, spent as padding on the scrolling panel
                — which reaches both shapes of screen at once, the ones that
                scroll in the panel and the ones that fill it and scroll inside
                themselves. See `.page-panel` in `index.css`.

                It used to end where the bar began, with each list keeping its
                own `pb-4` and nothing else. That is the cheaper layout and it
                was the right one until the bar became glass: frosting a pill
                that has nothing passing under it only tints it.

                `PageTransition` is the outlet, in its own scrolling panel — and
                on a phone the slide from one screen to the next, which is why
                the scroller is its rather than this shell's. */}
            <TabBarVisibilityContext.Provider value={visibility}>
              {/* Rebuilt per render rather than memoised: everything under this
                  provider is a child of the shell already, so it re-renders
                  with it whatever this value does. */}
              <AccountMenuContext.Provider
                value={{ open: openMenu, isOpen: menuOpen }}
              >
                <PageTransition
                  panelRef={setPanel}
                  // The one screen in this shell that is *arrived at* rather
                  // than simply there: the paid step of the way in is opened
                  // from the offer, which is outside this layout — so the
                  // shell mounts with it and there is no screen for the
                  // transition to have left. It pushes in from the right all
                  // the same, because to the reader it is the next step of a
                  // flow and not a new place.
                  enterOnMount={wayIn}
                />
              </AccountMenuContext.Provider>
            </TabBarVisibilityContext.Provider>
          </PullFrame>

          {barShown && <MobileTabBar />}
        </div>

        {/* The account, and everything the bar below has no room for. Over the
            shell rather than inside the page's column, since it stands in for
            a screen — see `MobileAccountMenu`. */}
        {menuOpen && <MobileAccountMenu onClose={closeMenu} />}
      </div>
    </UnsavedChangesProvider>
  );
};

export default AppLayout;
